import React, { useMemo, useState } from 'react';

import { gear } from '@/assets/icons';
import { ILayoutProps } from '@/components/Layout';
import { getZonedStartDateTimeISO } from '@/components/Layout/components/CreateSetlistPanel';
import {
	InitialSetlistGenerateSettings,
	RequiredSetlistGenerateSettings,
} from '@/components/Layout/components/GenerateSetlist';
import { SetlistSummaryCalendar } from '@/components/SetlistSummaryCard';
import {
	BottomCard,
	BottomCardAction,
	BottomCardForm,
} from '@/components/shared/BottomCard/BottomCard';
import { AsaphButton } from '@/components/shared/Button';
import { Flex } from '@/components/shared/Flex/Flex';
import { FormInput } from '@/components/shared/Inputs';
import { BiblePassageInput } from '@/components/Layout/components/GenerateSetlist/components/BiblePassageInput/BiblePassageInput';
import { Label } from '@/components/shared/Inputs/Label';
import { Select, SelectProps } from '@/components/shared/Inputs/Select';
import { SettingsListItems } from '@/components/shared/SettingCard';
import {
	UpgradeGateSheet,
	useGateAccepted,
} from '@/components/shared/UpgradeGateSheet';
import { DAYS_OF_WEEK } from '@/config/constants';
import {
	Permissions,
	useCreateTeamServiceMutation,
	useGetBiblePassageAnalysisMutation,
} from '@/graphql';
import { FetchError } from '@/graphql/codegen-fetcher';
import { hr24to12 } from '@/helpers/timeFormatter';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { useEnvironment } from '@/providers/EnvironmentProvider';
import { usePermissions } from '@/providers/PermissionsProvider';
import { useServices } from '@/providers/ServicesProvider';
import constants from '@/theme/exportedVariables.module.scss';
import { Capacitor } from '@capacitor/core';
import { isPlatform } from '@ionic/core';
import {
	CircularProgress,
	FormHelperText,
	InputAdornment,
	SvgIcon,
	useMediaQuery,
} from '@material-ui/core';
import { JsonViewer } from '@textea/json-viewer';
import { addHours, Day, format, getDay, nextDay } from 'date-fns';
import { getTimezoneOffset } from 'date-fns-tz';
import { useSnackbar } from 'notistack';

import styles from './GenerateSettingsBottomPanel.module.scss';
import { SecondStep } from './SecondStep/SecondStep';

type UpdatedSetlistGenerateSettings = RequiredSetlistGenerateSettings & {
	selectedSongIds?: string[];
};

const isCapacitor = Capacitor.isNativePlatform();
const isIOS = isPlatform('ios') && isCapacitor;
// const isCapacitor = true;

export interface GenerateSettingsBottomPanelProps {
	hasCloseButton?: boolean;
	saveLabel?: string;
	saveColor?: 'blue' | 'grey' | 'white' | 'gradient';
	title?: string;
	generateSettings?: InitialSetlistGenerateSettings;
	hasGenerate?: boolean;
	reasoning?: object;
	lastGeneratePositions?: object;
	onSave: (
		updatedSettings: UpdatedSetlistGenerateSettings,
		generateOnOpen: boolean,
	) => void;
	onClose?: () => void;
	openSonglistWithCallback?: ILayoutProps['openSonglistWithCallback'];
	setAccountOpen?: ILayoutProps['setAccountOpen'];
	config?: GenerateSettingsConfig;
}

type GenerateSettingsConfig = {
	fromOnboarding?: boolean;
};

const GATE_NAME = 'ai_full_history';

export const GenerateSettingsBottomPanel: React.FC<
	GenerateSettingsBottomPanelProps
> = ({
	hasCloseButton,
	saveLabel,
	saveColor,
	generateSettings: initialGenerateSettings,
	title,
	hasGenerate = true,
	reasoning,
	lastGeneratePositions,
	config,
	onSave,
	onClose,
	openSonglistWithCallback = () => {},
	setAccountOpen = () => {},
}) => {
	const { services, refetch: refetchServices } = useServices();
	const { environment } = useEnvironment();
	const isTablet = useMediaQuery(`(min-width: ${constants.sizeTablet}`);
	const { hasPermission } = usePermissions();
	const { enqueueSnackbar } = useSnackbar();
	const { handleError } = useErrorHandler();
	const isFullHistoryAccepted = useGateAccepted(GATE_NAME);
	const fireSelectSongs = useAnalyticsEvent(
		'Generate_Setlist_Bottom_Select_Songs',
	);

	const [showFullHistoryGate, setShowFullHistoryGate] = useState(false);

	const { mutateAsync: createService } = useCreateTeamServiceMutation();

	const fromOnboarding = config?.fromOnboarding || false;

	const [generateSettings, setGenerateSettings] = useState<
		Omit<InitialSetlistGenerateSettings, 'totalSongPositions'> & {
			totalSongPositions: NonNullable<
				InitialSetlistGenerateSettings['totalSongPositions']
			>;
		}
	>({
		serviceId: services[0]?.id,
		startTime: services[0]?.startAt,
		startDate:
			services[0] &&
			nextDay(new Date(), services[0].day as Day).toISOString(),
		totalSongPositions: 4,
		postSermonSongPosition: 3,
		searchTerm: '',
		bibleReference: undefined,
		...initialGenerateSettings,
	});
	const [setlistSongLengths, setSetlistSongLengths] = useState<{
		pre: number;
		post: number;
	}>({
		pre: (initialGenerateSettings?.postSermonSongPosition || 3) - 1,
		post:
			(initialGenerateSettings?.totalSongPositions || 4) -
			((initialGenerateSettings?.postSermonSongPosition || 3) - 1),
	});
	const [touched, setTouched] = useState(false);
	const [isServicesPanelOpen, setIsServicesPanelOpen] = useState(false);
	const [startTime, setStartTime] = useState(
		generateSettings?.startTime || '',
	);
	const [startDate, setStartDate] = useState(
		generateSettings?.startDate || '',
	);
	// const [selectedSongIds, setSelectedSongIds] = useState<string[]>(
	// 	config.selectedSongIds || [],
	// );
	// String represents a selected service, null represents custom service and undefined represents an empty state
	const [selectedServiceId, setSelectedServiceId] = useState<
		string | null | undefined
	>(generateSettings?.serviceId);
	// const [bookCode, setBookCode] = useState<string | undefined>();
	// const [bookChapterId, setBookChapterId] = useState<number | undefined>();
	// const [bookVerse, setBookVerse] = useState<number | undefined>();

	const IANATimeZone = useMemo(
		() => Intl.DateTimeFormat().resolvedOptions().timeZone,
		[],
	);

	const {
		isLoading: isBiblePassageLoading,
		mutateAsync: getBiblePassage,
		error: biblePassageError,
		isError: isBiblePassageError,
		reset: resetBiblePassage,
	} = useGetBiblePassageAnalysisMutation();

	let biblePassageErrorMessage = 'Placeholder';
	if (isBiblePassageError && biblePassageError instanceof FetchError) {
		biblePassageErrorMessage = biblePassageError.message;
	}
	// const { data: booksQuery, isLoading: isBooksLoading } =
	// 	useListBibleBooksQuery();
	// const { data: chaptersQuery, isLoading: isChaptersLoading } =
	// 	useListBibleChaptersQuery(
	// 		{
	// 			bookCode: bookCode as string,
	// 		},
	// 		{
	// 			enabled: typeof bookCode === 'string',
	// 		},
	// 	);
	// const { data: versesQuery, isLoading: isVersesLoading } =
	// 	useListBibleVersesQuery(
	// 		{
	// 			chapterId: bookChapterId as number,
	// 		},
	// 		{
	// 			enabled: typeof bookChapterId === 'number',
	// 		},
	// 	);

	const offsetInHours =
		getTimezoneOffset(IANATimeZone, new Date()) / 1000 / 60 / 60;
	const UTCOffset = `GMT${offsetInHours >= 0 ? '+' : ''}${offsetInHours}`;

	const serviceSettingItems: SettingsListItems = services.map((s) => ({
		type: 'setting',
		title: s.name,
		onClick: () => {},
		noChevron: true,
		disabled: !hasPermission(Permissions.ServiceUpdate),
		trailingText: `${DAYS_OF_WEEK[s.day]}, ${hr24to12(s.startAt)}`,
	}));

	const selectedService = services.find((s) => s.id === selectedServiceId);

	const createSetlist = async (
		generateOnOpen: boolean,
		selectedSongIds?: string[],
	) => {
		if (generateSettings?.bibleReference) {
			await getBiblePassage({
				reference: generateSettings.bibleReference,
			});
		}

		let finalServiceId = generateSettings?.serviceId;
		let finalStartTime = generateSettings?.startTime;
		let finalStartDate = generateSettings?.startDate;

		// Needed when the user can't see the option to pick a service (onboaring only)
		if (fromOnboarding) {
			if (!services.length) {
				try {
					const service = await createService({
						input: {
							day: getDay(Date.now()),
							name: 'Demo service',
							startAt: format(addHours(Date.now(), -1), 'HH:mm'),
						},
					});
					await refetchServices();
					finalServiceId = service.createTeamService.id;
					finalStartDate = new Date().toISOString();
					finalStartTime = service.createTeamService.startAt;
				} catch (error) {
					handleError({
						errorCode: 'GenerateDemoSetlistError',
						friendlyMessage: 'Failed to create demo service',
						error,
					});
					return;
				}
			} else {
				// Needed when the user can't see the option to pick a service (onboaring only)
				finalServiceId = services[0].id;
				finalStartDate = new Date().toISOString();
				finalStartTime = format(addHours(Date.now(), -1), 'HH:mm');
			}
		}

		if (
			generateSettings?.totalSongPositions &&
			finalStartTime &&
			finalStartDate &&
			finalServiceId !== undefined &&
			!isBiblePassageLoading &&
			!isBiblePassageError
		) {
			const finalSettings: UpdatedSetlistGenerateSettings = {
				totalSongPositions: selectedSongIds
					? selectedSongIds.length
					: generateSettings.totalSongPositions,
				postSermonSongPosition: selectedSongIds
					? selectedSongIds.length
					: generateSettings.postSermonSongPosition,
				startDate: finalStartDate,
				startTime: finalStartTime,
				serviceId: finalServiceId,
				bibleReference: generateSettings.bibleReference,
				searchTerm: generateSettings.searchTerm,
				selectedSongIds,
			};

			return onSave(finalSettings, generateOnOpen);
		} else {
			if (!isBiblePassageLoading && isBiblePassageError) {
				enqueueSnackbar(
					'Bible passage failed to load, please try again',
				);
			} else {
				enqueueSnackbar('Please fill in all the required fields');
			}
		}
	};

	const handleSelectSongs = () => {
		fireSelectSongs();
		if (saveLabel) {
			createSetlist(false);
			return;
		}
		openSonglistWithCallback(
			(songIds, navigatedAway) => {
				if (songIds.length && !navigatedAway) {
					createSetlist(false, songIds);
				}
			},
			{
				title: `Select ${generateSettings.totalSongPositions} Songs`,
				actionLabel: 'Create setlist',
				actionLabelVerb: 'Select',
				maxSelection: generateSettings.totalSongPositions,
			},
		);
	};

	const isFormValid =
		fromOnboarding ||
		Boolean(
			generateSettings?.totalSongPositions &&
				generateSettings?.startTime &&
				generateSettings?.startDate &&
				generateSettings?.serviceId !== undefined,
		);

	return (
		<>
			<BottomCard
				hasCloseButton={true}
				closeButtonLabel="Cancel"
				title={title || 'Create Setlist'}
				className={styles['root']}
				onClose={onClose}
			>
				<BottomCardForm>
					{fromOnboarding ? undefined : (
						<>
							<Label className={styles['label']} withOffset>
								Service
							</Label>
							<FormInput
								name="date"
								id="date"
								placeholder="Select a regular or custom service"
								value={
									selectedServiceId === null
										? 'Custom service'
										: selectedService?.name
								}
								inputClassName={styles['service-settings']}
								onClick={() => setIsServicesPanelOpen(true)}
								startAdornment={
									startTime && startDate ? (
										<SetlistSummaryCalendar
											className={
												styles['service-field-calendar']
											}
											startAtISO={getZonedStartDateTimeISO(
												startTime,
												startDate,
											)}
											size="md"
										/>
									) : null
								}
								// onBlur={handleBlur}
								endAdornment={
									<InputAdornment
										position="end"
										classes={{
											root: styles[
												'service-field-adornment'
											],
										}}
									>
										<SvgIcon component={gear} />
									</InputAdornment>
								}
							/>
							<div className="vsb-20" />
						</>
					)}

					<Flex gap={5} flexDirection="row">
						<Flex
							flexDirection="column"
							justifyContent="space-between"
							style={{ flex: 1 }}
						>
							<Label
								withOffset
								tooltipHeading="Pre-sermon songs"
								tooltip="The number of songs in your set **BEFORE** the sermon or main talk."
							>
								Pre-songs
							</Label>
							<Select
								onChange={(newvalue) => {
									if (typeof newvalue === 'number') {
										setTouched(true);
										setSetlistSongLengths((state) => ({
											...state,
											pre: newvalue,
										}));

										setGenerateSettings({
											...generateSettings,
											totalSongPositions:
												setlistSongLengths.post +
												newvalue,
											postSermonSongPosition:
												newvalue + 1,
										});
									}
								}}
								placeholder="Select number of songs"
								value={setlistSongLengths.pre.toString()}
								withBorder
								items={generateSongItems(4)}
								dropdownClassName={styles['select']}
								listClassName={styles['list']}
							/>
						</Flex>
						<Flex
							flexDirection="column"
							justifyContent="space-between"
							style={{ flex: 1 }}
						>
							<Label
								withOffset
								tooltipHeading="Post-sermon songs"
								tooltip="The number of songs in your set **AFTER** the sermon or main talk."
							>
								Post songs
							</Label>
							<Select
								onChange={(newvalue) => {
									if (typeof newvalue === 'number') {
										setTouched(true);
										setSetlistSongLengths((state) => ({
											...state,
											post: newvalue,
										}));

										setGenerateSettings({
											...generateSettings,
											totalSongPositions:
												setlistSongLengths.pre +
												newvalue,
										});
									}
								}}
								placeholder="None"
								value={setlistSongLengths.post.toString()}
								withBorder
								items={generateSongItems(4)}
								dropdownClassName={styles['select']}
								listClassName={styles['list']}
							/>
						</Flex>
					</Flex>
					<div className="vsb-20" />

					{/* <Label withOffset>Keywords</Label>
					<FormInput
						placeholder="Comma separated keywords"
						name="keywords"
						id="keywords"
						autoComplete="off"
						value={generateSettings?.searchTerm}
						onChange={(e) => {
							setTouched(true);
							setGenerateSettings({
								...generateSettings,
								searchTerm: e.currentTarget.value,
							});
						}}
					/>
					<div className="vsb-20" /> */}

					<Label
						withOffset
						tooltipHeading="Bible passage"
						tooltip="Match your post-sermon song to the primary passage planned for the sermon. Enter the passage using standard bible citation **Book chapter:verses** (e.g. John 3:16-21)."
					>
						Bible Passage (optional)
					</Label>
					<div style={{ position: 'relative' }}>
						<BiblePassageInput
							placeholder="Genesis 1:1-10"
							name="bibleReference"
							id="bibleReference"
							value={generateSettings?.bibleReference ?? ''}
							onChange={(value) => {
								setTouched(true);
								resetBiblePassage();
								setGenerateSettings({
									...generateSettings,
									bibleReference: value,
								});
							}}
							onError={resetBiblePassage}
							error={isBiblePassageError}
						/>
					</div>
					{isBiblePassageError && (
						<FormHelperText error={true}>
							{biblePassageErrorMessage}
						</FormHelperText>
					)}

					{/* <Flex gap={10} className="mt-sm">
						<Select
							className={styles['book-select']}
							items={
								booksQuery?.listBibleBooks.map((b) => ({
									label: b.name,
									value: b.code,
								})) || []
							}
							value={bookCode}
							fullDropdownWidth={false}
							onChange={(value) => setBookCode(value as string)}
						/>

						<Select
							className={styles['chapter-select']}
							items={
								chaptersQuery?.listBibleChapters.map((c) => ({
									label: c.number.toString(),
									value: c.id,
								})) || []
							}
							value={bookChapterId}
							onChange={(value) =>
								setBookChapterId(value as number)
							}
						/>

						<Select
							className={styles['verse-select']}
							items={
								versesQuery?.listBibleVerses.map((v) => ({
									label: v.number.toString(),
									value: v.number,
								})) || []
							}
							value={bookVerse}
							onChange={(value) => setBookVerse(value as number)}
						/>
					</Flex> */}
					{hasPermission(Permissions.DevtoolsRead) && (
						<div>
							{lastGeneratePositions && (
								<div>
									<Label withOffset>
										Last generated positions
									</Label>
									<JsonViewer
										value={lastGeneratePositions}
										quotesOnKeys={false}
										displayDataTypes={false}
										enableClipboard={false}
										defaultInspectDepth={1}
									/>
								</div>
							)}
							{reasoning && (
								<div>
									<Label withOffset>Reasoning</Label>
									<JsonViewer
										value={reasoning}
										quotesOnKeys={false}
										displayDataTypes={false}
										enableClipboard={false}
										defaultInspectDepth={1}
									/>
								</div>
							)}
						</div>
					)}
				</BottomCardForm>
				<BottomCardAction>
					<div className={styles['action-stack']}>
						{isBiblePassageLoading ? (
							<Flex
								justifyContent="center"
								alignItems="center"
								fullWidth
							>
								<CircularProgress size={40} />
							</Flex>
						) : (
							<div className={styles['button-row']}>
								<AsaphButton
									onClick={() => handleSelectSongs()}
									fullWidth
									label={saveLabel || 'Select songs'}
									color={saveColor || 'grey'}
									disabled={
										!isFormValid ||
										(!!saveLabel && !touched)
									}
								/>

								{hasGenerate ? (
									<AsaphButton
										onClick={() => createSetlist(true)}
										color="gradient"
										label="Generate Set"
										fullWidth
										disabled={!isFormValid}
									/>
								) : null}
							</div>
						)}
						{hasGenerate && !isFullHistoryAccepted && (
							<p className={styles['ai-data-note']}>
								Based on your last 30 days.{' '}
								<span
									className={styles['ai-data-note__link']}
									onClick={() => setShowFullHistoryGate(true)}
								>
									Unlock Full History
								</span>
							</p>
						)}
					</div>
				</BottomCardAction>
			</BottomCard>

			{isServicesPanelOpen && (
				<BottomCard
					hasCloseButton={hasCloseButton}
					title="Service Settings"
					onClose={() => setIsServicesPanelOpen(false)}
				>
					<SecondStep
						startTime={startTime}
						startDate={startDate}
						setStartDate={(val) => {
							setTouched(true);
							setStartDate(val);
						}}
						setStartTime={(val) => {
							setTouched(true);
							setStartTime(val);
						}}
						setSelectedServiceId={(val) => {
							setTouched(true);
							setSelectedServiceId(val);
						}}
						selectedServiceId={selectedServiceId}
						onSave={() => {
							setTouched(true);
							setGenerateSettings({
								...generateSettings,
								serviceId: selectedServiceId,
								startTime,
								startDate,
							});
							setIsServicesPanelOpen(false);
						}}
						setAccountOpen={setAccountOpen}
					/>
				</BottomCard>
			)}

			<UpgradeGateSheet
				open={showFullHistoryGate}
				onClose={() => setShowFullHistoryGate(false)}
				heading="Unlimited data history"
				body="Asaph generates better setlists the more it knows your church. Continue with unlimited history."
				gateName={GATE_NAME}
				onSkip={() => setShowFullHistoryGate(false)}
				rememberAcceptance={true}
			/>
		</>
	);
};

const generateSongItems = (num: number): SelectProps['items'] => {
	const items: SelectProps['items'] = [];
	for (let i = 0; i <= num; i++) {
		items.push({
			label: i.toString(),
			value: i,
		});
	}
	return items;
};
